import React, { useState, useEffect } from "react";
import { useFormik } from 'formik';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Grid, TextField,
    Button, Box, Typography, FormControl, InputLabel, Select, MenuItem,
    InputAdornment, IconButton
} from '@mui/material';
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import EditVendorvalidationSchema from '../../ValidationSchema/EditVendorValidation';
import api from 'core/interceptors/api';
import Swal from 'sweetalert2';
import KeywordsInput from 'layouts/tables/Product_Details/KeywordsInput';
import {
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';

const business_type = [
    { id: 1, category_name: 'Retail' },
    { id: 2, category_name: 'Wholesale' },
    { id: 3, category_name: 'Retail & Wholesale' },
];

const EditVendorModal = ({ open, handleClose, editInput, data }) => {
    const [showPassword, setShowPassword] = useState(false);

    const handleClickShowPassword = () => setShowPassword((show) => !show);

    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    const initialValues = {
        ...editInput,
        keywords: Array.isArray(editInput.keywords) ? editInput.keywords : (editInput.keywords ? editInput.keywords.split(',') : []),
        business_type: editInput.business_type || '',
        business_category: editInput.business_category || '',
        // Ensure 'account_no' is initialized correctly.
        // If your backend or incoming `editInput` uses 'account_number',
        // we map it to 'account_no' for Formik's internal state.
        account_no: editInput.account_no || editInput.account_number || '', // Prefer 'account_no', fallback to 'account_number'
    };

    const formik = useFormik({
        initialValues: initialValues,
        validationSchema: EditVendorvalidationSchema,
        onSubmit: async (values) => {
            console.log('Form Values BEFORE mapping for API:', values); // Debug: Check Formik's internal values

            try {
                const submitValues = {
                    ...values,
                    keywords: Array.isArray(values.keywords) ? values.keywords.join(',') : values.keywords
                };

                // ***** THE CRITICAL CHANGE FOR ACCOUNT NUMBER UPDATE *****
                // If your backend expects 'account_number' but Formik uses 'account_no':
                submitValues.account_number = submitValues.account_no; // Map the Formik field to the backend's expected field
                delete submitValues.account_no; // Remove the 'account_no' field if the backend strictly expects 'account_number' only

                console.log('Payload sent to API:', submitValues); // Debug: Check the actual payload being sent
                const response = await api.put('/vendor_register/update_vendor', submitValues, {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });

                if (response.data.status === 1) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Vendor Updated Successfully!',
                       
                        timer: 2000,
                        timerProgressBar: true,
                        showConfirmButton: false,
                    });
                    formik.resetForm();
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: 'Update Failed',
                        text: response.data.message || 'Failed to update vendor. Please try again.',
                    });
                }
                console.log('API Response:', response.data);
                handleClose();
            } catch (error) {
                console.error('Error updating vendor:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'An unexpected error occurred while updating the vendor. Please try again.',
                });
            }
        },
        enableReinitialize: true,
    });

    useEffect(() => {
        if (editInput) {
            formik.setValues({
                ...editInput,
                keywords: Array.isArray(editInput.keywords) ? editInput.keywords : (editInput.keywords ? editInput.keywords.split(',') : []),
                business_type: editInput.business_type || '',
                business_category: editInput.business_category || '',
                // Ensure 'account_no' is set for Formik's internal state when `editInput` changes
                account_no: editInput.account_no || editInput.account_number || '',
            });
        }
    }, [editInput, formik.setValues]); // Add formik.setValues to dependencies for useEffect, though it's stable


    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
            <DialogTitle>Edit Vendor</DialogTitle>
            <form onSubmit={formik.handleSubmit}>
                <DialogContent>
                    <Grid container spacing={2}>
                        {/* Basic Information */}
                        <Grid item xs={12}>
                            <Box sx={{ backgroundColor: '#e3f2fd', p: 2, borderRadius: 1, mb: 2 }}>
                                <Typography variant="h6" gutterBottom>Basic Information</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Business Name"
                                            name="business_name"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.business_name}
                                            fullWidth
                                            margin="normal"
                                            error={formik.touched.business_name && Boolean(formik.errors.business_name)}
                                            helperText={formik.touched.business_name && formik.errors.business_name}
                                            InputProps={{
                                                style: { borderColor: 'black' },
                                            }}
                                            variant="outlined"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth margin="normal">
                                            <InputLabel id="business_type">Business Type</InputLabel>
                                            <Select
                                                labelId="business-type-label"
                                                label="Business Type"
                                                name="business_type"
                                                value={formik.values.business_type}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                sx={{ height: '45px' }}
                                                error={formik.touched.business_type && Boolean(formik.errors.business_type)}
                                            >
                                                {business_type.map((category) => (
                                                    <MenuItem key={category.id} value={category.category_name}>
                                                        {category.category_name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            {formik.touched.business_type && formik.errors.business_type && (
                                                <Typography color="error" variant="body2">{formik.errors.business_type}</Typography>
                                            )}
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth margin="normal">
                                            <InputLabel id="business_category">Business Category</InputLabel>
                                            <Select
                                                labelId="business-category-label"
                                                label="Business Category"
                                                name="business_category"
                                                value={formik.values.business_category}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                sx={{ height: '45px' }}
                                                error={formik.touched.business_category && Boolean(formik.errors.business_category)}
                                            >
                                                {data.map((datas) => (
                                                    <MenuItem key={datas.id} value={datas.business_category}>
                                                        {datas.business_category}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            {formik.touched.business_category && formik.errors.business_category && (
                                                <Typography color="error" variant="body2">{formik.errors.business_category}</Typography>
                                            )}
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth margin="normal" error={formik.touched.keywords && Boolean(formik.errors.keywords)}>
                                            <KeywordsInput
                                                keywords={Array.isArray(formik.values.keywords) ? formik.values.keywords : (formik.values.keywords ? formik.values.keywords.split(',') : [])}
                                                onChange={(newKeywords) => {
                                                    formik.setFieldTouched("keywords", true);
                                                    formik.setFieldValue("keywords", newKeywords.join(','));
                                                }}
                                                onBlur={() => formik.setFieldTouched("keywords", true)}
                                            />
                                            {formik.touched.keywords && formik.errors.keywords && (
                                                <Typography color="error" variant="body2">
                                                    {formik.errors.keywords}
                                                </Typography>
                                            )}
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Owner Name"
                                            name="owner_name"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.owner_name}
                                            fullWidth
                                            margin="normal"
                                            error={formik.touched.owner_name && Boolean(formik.errors.owner_name)}
                                            helperText={formik.touched.owner_name && formik.errors.owner_name}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Email"
                                            name="email"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.email}
                                            fullWidth
                                            margin="normal"
                                            error={formik.touched.email && Boolean(formik.errors.email)}
                                            helperText={formik.touched.email && formik.errors.email}
                                            disabled
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Mobile Number"
                                            name="mobile_number"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.mobile_number}
                                            fullWidth
                                            margin="normal"
                                            error={formik.touched.mobile_number && Boolean(formik.errors.mobile_number)}
                                            helperText={formik.touched.mobile_number && formik.errors.mobile_number}
                                            disabled
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Password"
                                            name="password"
                                            type={showPassword ? 'text' : 'password'}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.password}
                                            fullWidth
                                            margin="normal"
                                            error={formik.touched.password && Boolean(formik.errors.password)}
                                            helperText={formik.touched.password && formik.errors.password}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            aria-label="toggle password visibility"
                                                            onClick={handleClickShowPassword}
                                                            onMouseDown={handleMouseDownPassword}
                                                            edge="end"
                                                        >
                                                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        </Grid>

                        {/* Bank Details */}
                        <Grid item xs={12}>
                            <Box sx={{ backgroundColor: '#f3e5f5', p: 2, borderRadius: 1, mb: 2 }}>
                                <Typography variant="h6" gutterBottom>Bank Details</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Account Number"
                                            name="account_no" // Correct Formik field name
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.account_no} // Correct Formik value access
                                            fullWidth
                                            margin="normal"
                                            error={formik.touched.account_no && Boolean(formik.errors.account_no)}
                                            helperText={formik.touched.account_no && formik.errors.account_no}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="IFSC Code"
                                            name="ifsc_code"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.ifsc_code}
                                            fullWidth
                                            margin="normal"
                                            error={formik.touched.ifsc_code && Boolean(formik.errors.ifsc_code)}
                                            helperText={formik.touched.ifsc_code && formik.errors.ifsc_code}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Passbook Name"
                                            name="passbook_name"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.passbook_name}
                                            fullWidth
                                            margin="normal"
                                            error={formik.touched.passbook_name && Boolean(formik.errors.passbook_name)}
                                            helperText={formik.touched.passbook_name && formik.errors.passbook_name}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Bank Name"
                                            name="bank_name"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.bank_name}
                                            fullWidth
                                            margin="normal"
                                            error={formik.touched.bank_name && Boolean(formik.errors.bank_name)}
                                            helperText={formik.touched.bank_name && formik.errors.bank_name}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Branch Name"
                                            name="branch_name"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.branch_name}
                                            fullWidth
                                            margin="normal"
                                            error={formik.touched.branch_name && Boolean(formik.errors.branch_name)}
                                            helperText={formik.touched.branch_name && formik.errors.branch_name}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Pan Card Number"
                                            name="pan_card_number"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.pan_card_number}
                                            fullWidth
                                            margin="normal"
                                            error={formik.touched.pan_card_number && Boolean(formik.errors.pan_card_number)}
                                            helperText={formik.touched.pan_card_number && formik.errors.pan_card_number}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="GST Number"
                                            name="gst"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.gst}
                                            fullWidth
                                            margin="normal"
                                            error={formik.touched.gst && Boolean(formik.errors.gst)}
                                            helperText={formik.touched.gst && formik.errors.gst}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleClose} color="primary">
                        Cancel
                    </Button>
                    <MDButton type="submit" color="success">Save</MDButton>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default EditVendorModal;